import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label", "aria-labelledby"]

import { computed, inject, provide, ref, toRef, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import HintText from "../SharedForm/HintText.vue";
import ValidationText from "../SharedForm/ValidationText.vue";
import type { HTMLInputElementEvent, InputEventDetails } from "../../types/SharedInput.type";


export default /*@__PURE__*/_defineComponent({
  __name: 'SharedRadioGroup',
  props: {
    chip: { type: Boolean },
    innerClass: {},
    displayName: {},
    hint: {},
    hintPermanent: { type: Boolean },
    modelValue: {},
    name: {},
    required: { type: Boolean },
    forcedErrorMessage: {},
    requiredErrorMessage: {},
    label: {},
    ariaLabel: {},
    disableErrorMessages: { type: Boolean },
    dataTestName: { default: "" },
    displayModeVertical: { type: Boolean },
    displayModeHorizontal: { type: Boolean },
    displayModeHorizontalRadiobuttons: { type: Boolean }
  },
  emits: ["update:modelValue", "change-error-msg"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

const emit = __emit;

const checkedRadio = ref<string | null>(props.modelValue ? props.modelValue : null);

watchEffect(() => {
	emit("update:modelValue", checkedRadio.value || null);
});

watch(
	() => props.modelValue as string | null,
	(newValue) => {
		checkedRadio.value = newValue;
		updateFormValidity();
	}
);

const isValidationVisible = ref(!props.disableErrorMessages && !!props.forcedErrorMessage);

const updateFormValidity: () => boolean | void = inject("updateFormValidity", () => {});
const requiredMessage = computed(() => {
	if (!isValidationVisible.value) return "";
	const fieldName = props.displayName || props.name || t("shared.radio.defaultField");
	return (
		props.forcedErrorMessage || props.requiredErrorMessage || t("shared.radio.requiredError", { field: fieldName })
	);
});

const updateInputValidityState = (
	fieldErrorMessage: string | undefined,
	isValidationOnFieldVisible: boolean | undefined
) => {
	isValidationVisible.value =
		!props.disableErrorMessages && (!!props.forcedErrorMessage || !!isValidationOnFieldVisible);
};

provide("radioGroupName", props.name);
provide("isRadioGroupChip", props.chip);
provide("checkedRadio", checkedRadio);
provide("isRadioGroupRequired", toRef(props, "required"));
provide("forcedErrorMessage", props.forcedErrorMessage);
provide("updateInputValidityState", updateInputValidityState);
provide("updateCheckedRadio", (value: string) => {
	checkedRadio.value = value;
	updateFormValidity();
});

const isHintVisible = computed(() => props.hintPermanent || !isValidationVisible.value);

watch([isValidationVisible, requiredMessage], () => {
	emit("change-error-msg", event as HTMLInputElementEvent, { errorMessage: requiredMessage.value });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "tw text-sm",
    role: "radiogroup",
    "aria-label": _ctx.ariaLabel && !_ctx.label ? _ctx.ariaLabel : undefined,
    "aria-labelledby": _ctx.label ? 'salutation_label' : undefined
  }, [
    _createElementVNode("div", {
      class: _normalizeClass({ 'flex items-center gap-4': _ctx.displayModeHorizontal })
    }, [
      (_ctx.label)
        ? (_openBlock(), _createElementBlock("p", {
            key: 0,
            id: "salutation_label",
            class: _normalizeClass({ 'mb-2': !_ctx.displayModeHorizontal })
          }, _toDisplayString(_ctx.label), 3))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass([
					_ctx.innerClass,
					{
						'flex items-center gap-4': _ctx.displayModeHorizontalRadiobuttons || _ctx.displayModeHorizontal,
						'flex flex-wrap gap-2': _ctx.chip,
						'flex flex-col gap-2': _ctx.displayModeVertical,
					},
				])
      }, [
        _renderSlot(_ctx.$slots, "default")
      ], 2)
    ], 2),
    (_ctx.hint && isHintVisible.value)
      ? (_openBlock(), _createBlock(HintText, {
          key: 0,
          "hint-text": _ctx.hint,
          "no-indent": ""
        }, null, 8, ["hint-text"]))
      : _createCommentVNode("", true),
    (isValidationVisible.value)
      ? (_openBlock(), _createBlock(ValidationText, {
          key: 1,
          class: "pt-2",
          "data-test-name": _ctx.dataTestName,
          "error-message": requiredMessage.value,
          "no-indent": ""
        }, null, 8, ["data-test-name", "error-message"]))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})